import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';

import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';
import { MfgappsEventService } from 'src/app/services/mfgapps-event.service';
import { CostCalculatorComponent } from '../cost-calculator/cost-calculator.component';
import { EntryViewModel } from 'src/app/models/successEntryModel';
import { ExportSuccessModel } from 'src/app/models/exportSuccessModel';
import { ExportPowerpointComponent } from "../export-powerpoint/export-powerpoint.component";
import { UploadDocumentsComponent } from "../upload-documents/upload-documents.component";
import { Constants } from 'src/app/models/constants';

@Component({
  selector: 'app-view-entry',
  imports: [MatProgressSpinnerModule, MatIcon, MatTooltip, MatFormFieldModule, MatButtonModule,
    CostCalculatorComponent, ExportPowerpointComponent, UploadDocumentsComponent],
  templateUrl: './view-entry.component.html',
  styleUrl: './view-entry.component.scss'
})
export class ViewEntryComponent {
  window = window;
  entryDetails: EntryViewModel;
  contributorsList: { displayName: string, fullName: string, email: string }[] = [];
  loader = { isLoading: true, message: 'Loading...' };
  errorMessage = '';
  costCalculationPayload: any;
  imagesAndDocumentsPayload: any;
  downtimeMode = '';
  //Both ViewChild items are used to pass image data and configurations to PowerPoint export component.
  @ViewChild('images_container') imagesContainer: UploadDocumentsComponent;
  @ViewChild('calculator') costCalculator: CostCalculatorComponent;
  powerPointExportData: ExportSuccessModel;

  constructor(private readonly mfgappsApiService: MfgappsApiService, public readonly eventService: MfgappsEventService,
    private readonly router: Router, private readonly route: ActivatedRoute) { }

  ngOnInit() {
    const entryId = this.route.snapshot.paramMap.get('entry_id');
    if (entryId) {
      this.fetchEntryDetails(entryId);
    }
  }

  async fetchEntryDetails(entryId: any) {
    this.loader.isLoading = true;
    this.loader.message = 'Fetching Entry Details...';
    try {
      this.entryDetails = (await this.mfgappsApiService.getEntryDetails(entryId)).result;
      this.imagesAndDocumentsPayload = {
        title: 'Supporting Images and Documents',
        entryId: this.entryDetails.entry_id,
        isViewMode: true
      };
      this.entryDetails.issues = this.entryDetails.issues.replaceAll('\r', '').replaceAll('\n', '<p></p>');
      this.contributorsList = this.eventService.parseContributorsList(this.entryDetails);
      this.eventService.setOperationalFocusCategories(this.entryDetails);
      //entryDate used in calculator form to get fiscal year from Date object.
      this.entryDetails.entryDate = new Date(`${this.entryDetails.date}T00:00:00`);
      if (this.entryDetails.productivity && !this.entryDetails.data_type && this.entryDetails.calc_steps?.length > 0) {
        this.downtimeMode = this.entryDetails.calc_steps.some(each => each.display_type.startsWith('L')) ? 'Line' : 'Shop';
        this.costCalculationPayload = {
          entryDetails: this.entryDetails,
          costCalculationSteps: this.entryDetails.calc_steps,
          isViewMode: true
        }
      }
    }
    catch (error) {
      console.info(error);
      this.errorMessage = 'Error occurred while Fetching Entry Details, Please try again !';
      this.eventService.showSnackBar(this.errorMessage, 'error');
    }
    this.resetLoader();
  }

  copyLink(link: any) {
    navigator.clipboard.writeText(link)
      .then(() => this.eventService.showSnackBar('Copied to clipboard', 'success', 3000))
      .catch(() => this.eventService.showSnackBar('Failed to copy text', 'error', 3000));
  }

  onBack() {
    this.router.navigate([Constants.EntriesRoute], {
      queryParams: { entryId: this.entryDetails.entry_id }
    });
  }

  get canModifyEntry() {
    return !!this.entryDetails && this.eventService.checkUserAccessToEntry(this.entryDetails) !== '';
  }

  async onEdit() {
    if (!this.canModifyEntry) {
      this.eventService.showSnackBar('You are not Authorized to edit this entry !!', 'error');
      return;
    }
    this.router.navigate([`${Constants.EntryRoute}/${this.entryDetails.entry_id}/edit`]);
  }

  async onDelete() {
    if (!this.canModifyEntry) {
      this.eventService.showSnackBar('You are not Authorized to delete this entry !!', 'error');
      return;
    }
    if (await this.eventService.confirmDialog(`Are you sure you want to delete entry <b>'${this.entryDetails.title}'</b> ?`)) {
      await this.handleDeleteEntry();
    }
  }

  async handleDeleteEntry() {
    this.loader.message = 'Deleting Entry...';
    this.loader.isLoading = true;
    try {
      await this.mfgappsApiService.deleteEntry(this.entryDetails.entry_id);
      this.eventService.showSnackBar(`Entry '${this.entryDetails.title}' Deleted Successfully !`, 'success');
      this.router.navigate([Constants.EntriesRoute]);
    }
    catch (error) {
      console.info(error);
      this.eventService.showSnackBar('Error occurred while deleting entry. Please try again !', 'error');
    }
    this.resetLoader();
  }

  initiateExport() {
    this.powerPointExportData = {
      entry: this.entryDetails,
      imageFileCategories: this.imagesContainer.uploadDocumentsConfiguration,
      shopConfiguration: this.costCalculator?.shopConfiguration,
      lineConfiguration: this.costCalculator?.lineConfiguration,
      aggregatedDownTimeCosts: this.costCalculator?.aggregatedDownTimeCosts,
      exportInitiated: true
    };
  }

  onExportCompleted(exportSuccess: boolean) {
    this.powerPointExportData.exportInitiated = false;
    if (exportSuccess) {
      this.eventService.showSnackBar(`Exported Entry '${this.entryDetails.title}' to PowerPoint successfully !`, 'success');
    }
    else {
      this.eventService.showSnackBar(`Export Entry '${this.entryDetails.title}' to PowerPoint could not complete !`, 'error');
    }
    this.powerPointExportData = null;
  }

  resetLoader() {
    this.loader.isLoading = false;
    this.loader.message = '';
  }
}